import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Edit, Ban, CheckCircle, Loader2, Copy, ExternalLink, Calendar, CreditCard, Package, Tag, Building, DollarSign, AlertCircle, Info, MoreHorizontal, Calculator } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCurrency } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { useAuthContext } from '@/contexts/AuthContext';

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface ExternalId {
  id: string;
  entity_name: string;
  entity_id: string;
  system_name: string;
  external_id: string;
  created_at: string;
  updated_at: string;
}

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  family_id: string;
  price: number;
  currency: string;
  billing_frequency: string;
  sku?: string;
  vendor_id?: string;
  vendor_subscription_name?: string;
  vendor_part_number?: string;
  pricing_model?: string;
  unit_measure?: string;
  minimum_quantity?: number;
  maximum_quantity?: number;
  billing_model?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_period_duration?: number;
  service_type?: string;
  organization_id?: string;
  billing_cycles?: string;
  trial_period_days?: number;
  imported?: boolean;
  prices?: SubscriptionPlanPrice[];
  family?: SubscriptionPlanFamily;
  created_at?: string;
  updated_at?: string;
}

export default function SubscriptionPlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [externalIds, setExternalIds] = useState<ExternalId[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingExternalIds, setLoadingExternalIds] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
      fetchExternalIds();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscription_plans')
        .select(`
          *,
          prices:subscription_plan_prices (*),
          family:subscription_plan_families (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setPlan(data);
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar plano',
        description: 'Não foi possível carregar os detalhes do plano. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExternalIds = async () => {
    try {
      setLoadingExternalIds(true);
      const { data, error } = await supabase
        .from('external_ids')
        .select('*')
        .eq('entity_name', 'subscription_plans')
        .eq('entity_id', id);

      if (error) throw error;
      setExternalIds(data || []);
    } catch (error) {
      console.error('Error fetching external IDs:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar IDs externos',
        description: 'Não foi possível carregar os IDs externos. Por favor, tente novamente.',
      });
    } finally {
      setLoadingExternalIds(false);
    }
  };

  const handleEdit = () => {
    navigate(`/subscription-plans/edit/${id}`);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano inativado',
        description: 'O plano foi inativado com sucesso.',
      });

      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao inativar plano',
        description: 'Não foi possível inativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleActivate = async () => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano ativado',
        description: 'O plano foi ativado com sucesso.',
      });

      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao ativar plano',
        description: 'Não foi possível ativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleDuplicate = async () => {
    if (!plan) return;
    
    try {
      // Criar um novo plano com TODOS os dados disponíveis, exceto o ID
      const newPlan = {
        name: `${plan.name} (Cópia)`,
        description: plan.description,
        family_id: plan.family_id,
        // Não copiar SKU - deixar o banco gerar um novo automaticamente
        sku: null,
        pricing_model: plan.pricing_model,
        unit_measure: plan.unit_measure,
        minimum_quantity: plan.minimum_quantity,
        maximum_quantity: plan.maximum_quantity,
        billing_model: plan.billing_model,
        billing_period_type: plan.billing_period_type,
        billing_frequency: plan.billing_period_type, // Sincronizar com billing_period_type
        billing_period_duration: plan.billing_period_duration,
        subscription_period_type: plan.subscription_period_type,
        subscription_period_duration: plan.subscription_period_duration,
        vendor_id: plan.vendor_id,
        vendor_subscription_name: plan.vendor_subscription_name,
        vendor_part_number: plan.vendor_part_number,
        service_type: plan.service_type || 'licensing',
        organization_id: plan.organization_id, // Copiar organization_id se existir
        is_active: true,
        // Campos de auditoria - preencher explicitamente para garantir
        created_by: user?.id,
        updated_by: user?.id,
        imported: false, // Reset import flag for cloned plan
        // Campos de preço (se existirem na tabela principal)
        price: plan.price,
        currency: plan.currency,
        // Campos de cobrança adicionais
        billing_cycles: plan.billing_cycles,
        trial_period_days: plan.trial_period_days,
      };

      // Inserir o novo plano
      const { data: newPlanData, error: planError } = await supabase
        .from('subscription_plans')
        .insert(newPlan)
        .select()
        .single();

      if (planError) throw planError;

      // Copiar os preços
      if (plan.prices && plan.prices.length > 0) {
        const pricePromises = plan.prices.map(price => {
          const newPrice = {
            subscription_plan_id: newPlanData.id,
            currency: price.currency,
            price_amount: price.price_amount,
            cost_amount: price.cost_amount,
            margin: price.margin,
          };
          
          return supabase
            .from('subscription_plan_prices')
            .insert(newPrice);
        });

        await Promise.all(pricePromises);
      }

      // Criar produto no HubSpot para o plano clonado
      try {
        const { data: prices } = await supabase
          .from('subscription_plan_prices')
          .select('currency, price_amount')
          .eq('subscription_plan_id', newPlanData.id)
          .in('currency', ['BRL', 'USD']);

        const brlPrice = prices?.find(p => p.currency === 'BRL')?.price_amount || 0;
        const usdPrice = prices?.find(p => p.currency === 'USD')?.price_amount || 0;

        // Mapear service_type para hs_product_type
        const getHubSpotProductType = (serviceType: string) => {
          const normalizedType = serviceType?.trim()?.toLowerCase();
          if (normalizedType === 'licensing') return 'license';
          if (normalizedType === 'material') return 'material';
          if (normalizedType === 'program') return 'program';
          if (normalizedType === 'professional_service') return 'service';
          return 'service'; // Default
        };

        // Mapear billing_period_type para recurringbillingfrequency
        const getRecurringBillingFrequency = (billingPeriodType: string) => {
          const frequencyMap: Record<string, string> = {
            'month': 'monthly',
            'year': 'annually',
            'quarter': 'quarterly',
            'semiannual': 'semiannually',
            'one-time': 'one-time'
          };
          return frequencyMap[billingPeriodType] || 'monthly';
        };

        // Montar payload para HubSpot
        const hubspotPayload: any = {
          name: newPlanData.name,
          description: newPlanData.description || '',
          // Só incluir SKU se existir
          ...(newPlanData.sku && { hs_sku: newPlanData.sku }),
          hs_product_type: getHubSpotProductType(newPlanData.service_type || 'licensing'),
          hs_price_brl: brlPrice.toString(),
          ...(usdPrice > 0 && { hs_price_usd: usdPrice.toString() }),
        };

        // Adicionar frequência de cobrança apenas se não for one-time
        if (newPlanData.billing_period_type !== 'one-time') {
          hubspotPayload.recurringbillingfrequency = getRecurringBillingFrequency(newPlanData.billing_period_type || 'month');
        }

        // Chamar Edge Function para criar produto no HubSpot
        console.log('🚀 Chamando Edge Function create-product-hubspot com payload:', hubspotPayload);
        
        const { data: hubspotResult, error: hubspotError } = await supabase.functions.invoke('create-product-hubspot', {
          body: hubspotPayload
        });

        console.log('📥 Resposta da Edge Function:', { hubspotResult, hubspotError });

        if (hubspotError) {
          console.error('Erro ao criar produto no HubSpot:', hubspotError);
          toast({
            variant: 'default',
            title: 'Aviso',
            description: 'Plano duplicado com sucesso, mas houve um erro ao criar no HubSpot.',
          });
        } else if (hubspotResult && (hubspotResult.id || hubspotResult.data?.id)) {
          // Extrair o ID do produto do HubSpot da resposta
          const hubspotProductId = hubspotResult.id || hubspotResult.data?.id;
          console.log('✅ Produto criado no HubSpot com sucesso:', hubspotProductId);
          
          // Salvar o ID do HubSpot na tabela external_ids
          const { error: externalIdError } = await supabase
            .from('external_ids')
            .insert({
              entity_name: 'subscription_plans',
              entity_id: newPlanData.id,
              system_name: 'hubspot',
              external_id: hubspotProductId
            });

          if (externalIdError) {
            console.error('Erro ao salvar ID do HubSpot:', externalIdError);
            toast({
              variant: 'default',
              title: 'Aviso',
              description: 'Produto criado no HubSpot, mas houve um erro ao salvar a referência.',
            });
          } else {
            console.log('✅ Referência do HubSpot salva com sucesso');
            toast({
              title: 'Sucesso',
              description: 'Plano duplicado e sincronizado com HubSpot com sucesso!',
            });
          }
        } else {
          console.warn('Resposta do HubSpot não contém ID do produto:', hubspotResult);
          console.log('🔍 Estrutura completa da resposta:', JSON.stringify(hubspotResult, null, 2));
          toast({
            variant: 'default',
            title: 'Aviso',
            description: 'Plano duplicado com sucesso, mas resposta do HubSpot não contém ID do produto.',
          });
        }

      } catch (hubspotError) {
        console.error('Erro na integração com HubSpot:', hubspotError);
        // Não falhar o processo de clonagem por erro no HubSpot
      }

      toast({
        title: 'Plano duplicado',
        description: 'O plano foi duplicado com sucesso.',
      });

      // Redirecionar para a página de edição do novo plano
      navigate(`/subscription-plans/edit/${newPlanData.id}`);
    } catch (error) {
      console.error('Erro ao duplicar plano:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao duplicar plano',
        description: 'Não foi possível duplicar o plano. Por favor, tente novamente.',
      });
    }
  };

  const formatBillingFrequency = (frequency: string) => {
    switch (frequency) {
      case 'month':
        return 'Mensal';
      case 'quarter':
        return 'Trimestral';
      case 'semiannual':
        return 'Semestral';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return frequency;
    }
  };

  const formatBillingModel = (model?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'on creation':
        return 'Na criação';
      case 'on activation':
        return 'Na ativação';
      case 'on usage':
        return 'No uso';
      case 'subscription':
        return 'Assinatura';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return model;
    }
  };

  const formatPricingModel = (model?: string, unitMeasure?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'flat':
        return 'Valor Fixo';
      case 'per unit':
        return `Por Unidade${unitMeasure ? ` (${formatUnitMeasure(unitMeasure)})` : ''}`;
      default:
        return model;
    }
  };

  const formatBillingPeriod = (type?: string, duration?: number) => {
    if (!type || !duration) return '-';
    
    const typeLabel = type === 'month' ? 'Mês(es)' : type === 'year' ? 'Ano(s)' : type;
    return `${duration} ${typeLabel}`;
  };

  const formatUnitMeasure = (unitMeasure?: string) => {
    if (!unitMeasure) return '-';
    
    const unitMap: Record<string, string> = {
      'user': 'Por usuário',
      'hour': 'Por hora',
      'gb': 'Por GB',
      'license': 'Por licença',
      'seat': 'Por assento',
      'instance': 'Por instância',
      'api_call': 'Por chamada de API',
      'transaction': 'Por transação'
    };
    
    return unitMap[unitMeasure.toLowerCase()] || unitMeasure;
  };

  // Função para obter gradiente baseado no status
  const getStatusGradient = (isActive: boolean) => {
    return isActive 
      ? 'from-green-500 via-green-600 to-green-700'
      : 'from-gray-500 via-gray-600 to-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Plano</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <AlertCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Plano Não Encontrado</h1>
              <p className="text-white/80 mb-6">O plano solicitado não foi encontrado em nossos registros.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/subscription-plans')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para Planos
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Dynamic Gradient Based on Status */}
      <div className="relative bg-[#1F1746] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
          <Button 
                  variant="ghost" 
                  size="icon" 
            onClick={() => navigate('/subscription-plans')}
                  className="text-white hover:bg-white/20 border-white/30"
          >
                  <ArrowLeft className="h-5 w-5" />
          </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <Package className="h-6 w-6" />
                  <span className="text-white/80">Plano de Assinatura</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                {plan.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90 mb-4">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  <span>{plan.family?.name || 'Sem família'}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">
                    {plan.prices && plan.prices.length > 0 
                      ? formatCurrency(plan.prices[0].price_amount, plan.prices[0].currency)
                      : 'Preço não definido'
                    }
                  </span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                    <Badge 
                  className={`${plan.is_active 
                    ? 'bg-white/20 text-white border-white/30' 
                    : 'bg-red-500/20 text-red-100 border-red-300/30'
                  }`}
                >
                  {plan.is_active ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
              
              {plan.description && (
                <p className="text-white/80 text-lg max-w-2xl">
                  {plan.description}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
          <Button
                variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Gerenciar Plano</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Plano
                  </DropdownMenuItem>
                  {!plan.imported && (
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicar Plano
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
            onClick={plan.is_active ? handleDelete : handleActivate}
                    className={plan.is_active ? "text-red-600" : "text-green-600"}
          >
            {plan.is_active ? (
              <>
                    <Ban className="h-4 w-4 mr-2" />
                        Inativar Plano
              </>
            ) : (
              <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                        Ativar Plano
              </>
            )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
            <Button
            onClick={handleEdit}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
                <Edit className="h-4 w-4 mr-2" />
                Editar
          </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        
        {/* Main Content Grid - All sections in one view */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          
          {/* Column 1 - Plan Overview */}
          <div className="space-y-6">
            {/* Plan Overview */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Visão Geral do Plano
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Nome</span>
                    <span className="text-sm font-extrabold text-right max-w-[200px]">{plan.name}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">SKU</span>
                    <span className="text-sm font-extrabold font-mono">{plan.sku || 'Não definido'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Família</span>
                    <span className="text-sm font-extrabold">{plan.family?.name || 'Sem família'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Criado em</span>
                    <span className="text-sm font-extrabold">
                      {plan.created_at ? new Date(plan.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Atualizado em</span>
                    <span className="text-sm font-extrabold">
                      {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {plan.description && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="text-sm font-extrabold text-gray-600 mb-2">Descrição</h4>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-gray-900 text-sm leading-relaxed">{plan.description}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Column 2 - Pricing Model, Vendor Info & External IDs */}
          <div className="space-y-6">
            {/* Pricing Model */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-blue-600" />
                  Modelo de Precificação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1 bg-blue-100 rounded-md">
                      <Tag className="h-3 w-3 text-blue-600" />
                    </div>
                    <div className="font-extrabold text-blue-800 text-sm">
                      {formatPricingModel(plan.pricing_model, plan.unit_measure)}
                    </div>
                  </div>
                  <div className="text-xs text-blue-600">Modelo de precificação</div>
                </div>

                {plan.pricing_model === 'per unit' && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <h5 className="font-extrabold text-orange-800 mb-2 text-sm">Configuração por Unidade</h5>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-orange-600">Unidade:</span>
                        <span className="font-extrabold text-orange-800">
                          {formatUnitMeasure(plan.unit_measure)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600">Mín:</span>
                        <span className="font-extrabold text-orange-800">
                          {plan.minimum_quantity || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600">Máx:</span>
                        <span className="font-extrabold text-orange-800">
                          {plan.maximum_quantity || 'Ilimitada'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vendor Information */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-purple-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-5 w-5 text-purple-600" />
                  Informações do Fornecedor
                </CardTitle>
              </CardHeader>
              <CardContent>
                {plan.vendor_id ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">Nome da Assinatura</span>
                      <span className="text-sm font-extrabold text-right max-w-[150px]">
                        {plan.vendor_subscription_name || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">Número da Parte</span>
                      <span className="text-sm font-extrabold font-mono">
                        {plan.vendor_part_number || "-"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Building className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhum fornecedor associado</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* External IDs */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-indigo-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ExternalLink className="h-5 w-5 text-indigo-600" />
                  IDs Externos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingExternalIds ? (
                  <div className="flex justify-center items-center h-20">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <p className="text-muted-foreground text-xs">Carregando...</p>
                    </div>
                  </div>
                ) : externalIds.length > 0 ? (
                  <div className="space-y-3">
                    {externalIds.map((extId) => (
                      <div key={extId.id} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-sm font-extrabold text-indigo-800">{extId.system_name}</span>
                          <span className="text-xs text-indigo-600 font-mono">{extId.external_id}</span>
                        </div>
                        <div className="text-xs text-indigo-600">
                          Criado: {new Date(extId.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <ExternalLink className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhum ID externo</p>
                    <p className="text-xs text-gray-400">Criados automaticamente nas integrações</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Column 3 - Status, Pricing & Billing Information */}
          <div className="space-y-6">
            
            {/* Status & Configuration */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Info className="h-5 w-5 text-green-600" />
                  Status & Configuração
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <div className="text-sm font-extrabold text-green-800">Status do Plano</div>
                    <div className="text-xs text-green-600">Disponibilidade atual</div>
                  </div>
                  <Badge 
                    variant={plan.is_active ? "default" : "secondary"}
                    className={plan.is_active ? "bg-green-500 hover:bg-green-600" : "bg-gray-500 hover:bg-gray-600"}
                  >
                    {plan.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </div>

                {plan.imported && (
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div>
                      <div className="text-sm font-extrabold text-orange-800">Plano Importado</div>
                      <div className="text-xs text-orange-600">Criado via importação</div>
                    </div>
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      Importado
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing Section */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Estrutura de Preços
                </CardTitle>
              </CardHeader>
              <CardContent>
                {plan.prices && plan.prices.length > 0 ? (
                  <div className="space-y-3">
                    {plan.prices.map((price, index) => (
                      <div key={price.id} className="p-3 bg-white rounded-lg border border-green-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-green-700 border-green-300 text-xs">
                              {price.currency}
                            </Badge>
                            {index === 0 && (
                              <Badge className="bg-green-500 text-white text-xs">Principal</Badge>
                            )}
                          </div>
                          <div className="text-lg font-extrabold text-green-800">
                            {formatCurrency(price.price_amount, price.currency)}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-gray-500">Custo:</span>
                            <span className="font-extrabold ml-1">
                              {price.cost_amount ? formatCurrency(price.cost_amount, price.currency) : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Margem:</span>
                            <span className="font-extrabold ml-1 text-green-600">
                              {price.margin ? `${price.margin.toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <DollarSign className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhum preço configurado</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing Information */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-orange-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-orange-600" />
                  Informações de Cobrança
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Modelo de Cobrança</span>
                    <span className="text-sm font-extrabold">{formatBillingModel(plan.billing_model)}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Período de Cobrança</span>
                    <span className="text-sm font-extrabold">{formatBillingFrequency(plan.billing_period_type)}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Duração do Período</span>
                    <span className="text-sm font-extrabold">
                      {formatBillingPeriod(plan.billing_period_type, plan.billing_period_duration)}
                    </span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Período de Assinatura</span>
                    <span className="text-sm font-extrabold">
                      {formatBillingPeriod(plan.subscription_period_type, plan.subscription_period_duration)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
        
        {/* Bottom Spacing */}
        <div className="h-8" />
      </div>
    </div>
  );
} 